// Workspace events decorate the existing authenticated/resumable transport.
// A turn's workspace selection is captured BEFORE the async history mirror.
import type { UIMessage, UIMessageChunk } from 'ai';
import type { AssistantRuntime } from '@assistant-ui/react';
import { SubmittedTurnChatTransport } from './submitted-turn-transport';
import { prepareAnswerReplay } from './answer-replay';
import { answerFromStreamId } from '../../src/chat-progress/descriptor.mjs';
import { InspectFrozenResult } from '../../src/apps/selection/host-contracts.mjs';
import type { CapturedNativeSelection } from '../workspace/types';
import type { EditingAppSelection } from '../../src/apps/tracer/editing-base.mjs';

type Options = ConstructorParameters<typeof SubmittedTurnChatTransport>[0];
type Remember = ConstructorParameters<typeof SubmittedTurnChatTransport>[1];
type Send = Parameters<SubmittedTurnChatTransport['sendMessages']>[0];
type Resume = Parameters<SubmittedTurnChatTransport['reconnectToStream']>[0];
/**
 * Which tool call a result came from, and whether it arrived on a resumed
 * stream. A resumed stream replays the turn from its start, so it can deliver
 * a result this page already received (or one a reload has already reflected).
 */
export type ToolResultOrigin = { toolCallId: string; resumed: boolean };
type WorkspaceHooks = {
  context: () => unknown;
  /** Serving admission from the actual mounted Step6b reader, never an env/UI grant. */
  answerReaderAdmitted?: () => boolean;
  captureNativeSelection?: () => CapturedNativeSelection | null | undefined;
  captureEditingApp?: () => EditingAppSelection | null | undefined;
  event: (value: unknown) => void;
  artifact: (toolName: string, value: unknown, origin: ToolResultOrigin) => void;
  /** A tool call has started streaming (its name is known before its input or result). */
  toolStart?: (toolName: string, origin: ToolResultOrigin) => void;
  /** A new message was sent with the captured context: one-shot context (an App selection) is spent. */
  submitted?: (messageId?: string) => void;
  /** The turn was refused before it was sent (its chat can no longer change its App): `true` keeps its message
   * out of durable history; a later capture that succeeds for the same message gives `false`. */
  unsent?: (messageId: string, refused: boolean, code?: string) => void;
  voiceOutput?: (value: unknown, origin: { resumed: boolean }) => void;
  voiceToolTiming?: (value: unknown, origin: { resumed: boolean }) => void;
  /** Extra body fields captured once per sent turn, e.g. the suggested
   * follow-up the owner asked (collaboration v1: `suggestionId`). */
  extraBody?: (
    trigger: Send['trigger'],
    message: UIMessage | undefined,
  ) => {
    body: Record<string, unknown>;
    /** The server returned an answer stream; consume this capture after admission. */
    accepted?: () => void;
  } | null;
};
/** The chat edits an App it can no longer name, or whose preview is gone: the turn is not sent. */
export const EDITING_APP_UNAVAILABLE = 'editing_app_unavailable';
const EDITING_REFUSALS: ReadonlySet<unknown> = new Set([
  EDITING_APP_UNAVAILABLE,
  'editing_preview_unavailable',
]);
type SubmittedWorkspace = {
  messageId: string | undefined;
  context: unknown;
  selection: CapturedNativeSelection | null | undefined;
  frozen?: Promise<InspectFrozenResult>;
};

export function observeWorkspaceChunks(
  stream: ReadableStream<UIMessageChunk>,
  hooks: WorkspaceHooks,
  resumed = false,
) {
  const names = new Map<string, string>();
  return stream.pipeThrough(
    new TransformStream<UIMessageChunk, UIMessageChunk>({
      transform(chunk, controller) {
        if (chunk.type === 'data-accountSettings') {
          const receipt = chunk.data as { actionId?: unknown; result?: unknown } | null;
          if (typeof receipt?.actionId === 'string') {
            try {
              hooks.artifact('update_my_settings', receipt.result, {
                toolCallId: receipt.actionId,
                resumed,
              });
            } catch {
              /* The owner can re-read the persisted setting after a presentation failure. */
            }
          }
          return;
        }
        if (chunk.type === 'data-voiceToolTiming') {
          try {
            if (!resumed) hooks.voiceToolTiming?.(chunk.data, { resumed });
          } catch {
            /* Lane telemetry must not break the answer. */
          }
          // Timing is local presentation, never stored in native messages.
          return;
        }
        if (chunk.type === 'tool-input-start' || chunk.type === 'tool-input-available') {
          if (!names.has(chunk.toolCallId))
            try {
              hooks.toolStart?.(chunk.toolName, { toolCallId: chunk.toolCallId, resumed });
            } catch {
              /* presentation must not break the answer */
            }
          names.set(chunk.toolCallId, chunk.toolName);
        }
        if (chunk.type === 'data-workspace') {
          try {
            hooks.event(chunk.data);
          } catch {
            /* presentation must not break the answer */
          }
          return;
        }
        if (chunk.type === 'data-voiceOutput') {
          try {
            hooks.voiceOutput?.(chunk.data, { resumed });
          } catch {
            /* playback must not break the answer */
          }
          controller.enqueue(chunk);
          return;
        }
        if (chunk.type === 'tool-output-available') {
          const name = names.get(chunk.toolCallId);
          if (name) {
            try {
              hooks.artifact(name, chunk.output, { toolCallId: chunk.toolCallId, resumed });
            } catch {
              /* durable state can be reopened */
            }
          }
        }
        controller.enqueue(chunk);
      },
    }),
  );
}

export class WorkspaceChatTransport extends SubmittedTurnChatTransport {
  private submittedWorkspace: SubmittedWorkspace | null = null;
  private replayRuntime: AssistantRuntime | undefined;
  private readonly answerResumeSelected: () => ReturnType<typeof answerFromStreamId>;
  private readonly clearReadAddress: () => void;
  constructor(
    options: Options,
    remember: Remember,
    private readonly workspace: WorkspaceHooks,
  ) {
    let address: ReturnType<typeof answerFromStreamId> = null;
    const resume = options?.resumable;
    super(
      resume
        ? {
            ...options,
            resumable: {
              ...resume,
              storage: {
                getStreamId: () => resume.storage.getStreamId(),
                setStreamId: (id) => {
                  // The native transport calls this only for the actual
                  // response's x-resumable-stream-id. A legacy ID cannot
                  // activate new replay merely because config is enabled.
                  address = answerFromStreamId(id);
                  resume.storage.setStreamId(id);
                },
                clear: () => resume.storage.clear(),
              },
            },
          }
        : options,
      remember,
    );
    this.answerResumeSelected = () => address;
    this.clearReadAddress = () => {
      address = null;
    };
  }
  override setRuntime(runtime: AssistantRuntime) {
    super.setRuntime(runtime);
    this.replayRuntime = runtime;
  }
  override async sendMessages(options: Send) {
    const message = [...options.messages].reverse().find((message) => message.role === 'user');
    const messageId = message?.id;
    let captured: SubmittedWorkspace;
    try {
      captured =
        messageId && this.submittedWorkspace && this.submittedWorkspace.messageId === messageId
          ? this.submittedWorkspace
          : {
              messageId,
              context: this.captureContext(),
              selection:
                options.trigger === 'submit-message'
                  ? this.workspace.captureNativeSelection?.()
                  : null,
            };
    } catch (error) {
      if (messageId && EDITING_REFUSALS.has((error as Error | null)?.message))
        this.workspace.unsent?.(messageId, true, (error as Error).message);
      throw error;
    }
    if (messageId) this.workspace.unsent?.(messageId, false);
    if (captured.selection && !messageId) throw new TypeError('selection_turn_required');
    if (messageId && this.submittedWorkspace !== captured) {
      this.submittedWorkspace = captured;
      this.workspace.submitted?.(messageId);
    }
    const extra = this.workspace.extraBody?.(options.trigger, message);
    const stream = await super.sendMessages(options, async (submitted) => {
      if (captured.selection) {
        captured.frozen ??= captured.selection.freeze();
        const nativeSelection = InspectFrozenResult.parse(await captured.frozen);
        if (
          !captured.context ||
          typeof captured.context !== 'object' ||
          Array.isArray(captured.context)
        )
          throw new TypeError('selection_workspace_required');
        captured.context = { ...captured.context, nativeSelection };
      }
      // Projects v1: placement is a first message's alone; a regenerate never re-sends it.
      const workspace =
        options.trigger === 'submit-message'
          ? captured.context
          : withoutPlacement(captured.context);
      return {
        ...submitted,
        body: { ...submitted.body, ...(workspace ? { workspace } : {}), ...(extra?.body ?? {}) },
      };
    });
    extra?.accepted?.();
    return observeWorkspaceChunks(stream, this.workspace);
  }
  private captureContext() {
    const context = structuredClone(this.workspace.context());
    if (!this.workspace.captureEditingApp) return context;
    if (context != null && (typeof context !== 'object' || Array.isArray(context)))
      throw new TypeError('invalid_workspace_context');
    // The first Apps Create send can precede a canvas context. Its genuine
    // capture still distinguishes new work from an unavailable saved edit.
    const captured = context ?? {};
    const intent = (captured as { intent?: { mode?: string } }).intent;
    if (intent?.mode && intent.mode !== 'chat') return context;
    const editingApp = this.workspace.captureEditingApp();
    // A closed canvas is unselected, not fresh work. Discard any earlier
    // context locator; the current capture alone owns this turn's selection.
    const { editingApp: _previous, ...rest } = captured as Record<string, unknown>;
    return editingApp === undefined ? rest : { ...rest, editingApp: structuredClone(editingApp) };
  }
  override async reconnectToStream(options: Resume) {
    this.clearReadAddress();
    const stream = await super.reconnectToStream(options);
    const selected = this.answerResumeSelected();
    const replayAnswer = this.workspace.answerReaderAdmitted?.() === true && selected !== null;
    return stream
      ? observeWorkspaceChunks(
          replayAnswer
            ? await prepareAnswerReplay(stream, () => this.replayRuntime, selected?.answerId)
            : stream,
          this.workspace,
          true,
        )
      : stream;
  }
}

function withoutPlacement(context: unknown) {
  if (!context || typeof context !== 'object' || Array.isArray(context)) return context;
  if (!Object.hasOwn(context, 'projectId')) return context;
  const { projectId: _projectId, ...rest } = context as Record<string, unknown>;
  return rest;
}
